import React from 'react';
import { useStageStore } from '@/core/stores/stage-store';
import {
  NumberInput,
  SelectInput,
  TextInput,
  PanelSection,
  cn,
} from '@/ui/shared';
import type { Discipline, ScoringType } from '@/stage/types';

interface DisciplineConfig {
  value: string;
  label: string;
  scoringTypes: Array<{ value: string; label: string }>;
}

const disciplineConfig: DisciplineConfig[] = [
  {
    value: 'ipsc_handgun',
    label: 'IPSC Handgun',
    scoringTypes: [
      { value: 'comstock', label: 'Comstock' },
      { value: 'virginia_count', label: 'Virginia Count' },
    ],
  },
  {
    value: 'ipsc_rifle',
    label: 'IPSC Rifle',
    scoringTypes: [
      { value: 'comstock', label: 'Comstock' },
      { value: 'virginia_count', label: 'Virginia Count' },
    ],
  },
  {
    value: 'idpa',
    label: 'IDPA',
    scoringTypes: [
      { value: 'fixed_time', label: 'Fixed Time' },
      { value: 'vickers_count', label: 'Vickers Count' },
    ],
  },
  {
    value: 'uspsa',
    label: 'USPSA',
    scoringTypes: [
      { value: 'comstock', label: 'Comstock' },
      { value: 'fixed_time', label: 'Fixed Time' },
    ],
  },
  {
    value: 'steel_challenge',
    label: 'Steel Challenge',
    scoringTypes: [
      { value: 'fixed_time', label: 'Fixed Time' },
    ],
  },
];

const StageMetadataPanel: React.FC = () => {
  const { layout, updateMetadata, updateCanvas } = useStageStore();

  if (!layout) {
    return (
      <div className="h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 flex items-center justify-center">
        <p className="text-xs text-gray-500 dark:text-gray-400">No stage loaded</p>
      </div>
    );
  }

  const currentDiscipline = disciplineConfig.find(
    (d) => d.value === layout.metadata?.discipline
  );

  const scoringTypes = currentDiscipline?.scoringTypes || [];

  return (
    <div className="h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 overflow-y-auto">
      <div className="p-3 space-y-3">
        {/* Stage Information */}
        <PanelSection title="Stage Information" defaultOpen={true}>
          <TextInput
            label="Stage Name"
            value={layout.metadata?.name || ''}
            onChange={(value) => updateMetadata({ name: value })}
            placeholder="e.g., Stage 1"
          />
        </PanelSection>

        {/* Discipline and Scoring */}
        <PanelSection title="Discipline & Scoring" defaultOpen={true}>
          <SelectInput
            label="Discipline"
            value={layout.metadata?.discipline || ''}
            onChange={(value) => updateMetadata({ discipline: value as Discipline })}
            options={[
              { value: '', label: 'Select discipline...' },
              ...disciplineConfig.map((d) => ({
                value: d.value,
                label: d.label,
              })),
            ]}
          />

          {scoringTypes.length > 0 && (
            <SelectInput
              label="Scoring Type"
              value={layout.metadata?.scoring_type || ''}
              onChange={(value) =>
                updateMetadata({ scoring_type: value as ScoringType })
              }
              options={[
                { value: '', label: 'Select scoring type...' },
                ...scoringTypes,
              ]}
            />
          )}
        </PanelSection>

        {/* Canvas Settings */}
        <PanelSection title="Canvas" defaultOpen={true}>
          <NumberInput
            label="Canvas Width (m)"
            value={layout.canvas.width_m || 0}
            onChange={(value) => updateCanvas({ width_m: value })}
            step={0.5}
            min={1}
          />
          <NumberInput
            label="Canvas Height (m)"
            value={layout.canvas.height_m || 0}
            onChange={(value) => updateCanvas({ height_m: value })}
            step={0.5}
            min={1}
          />
        </PanelSection>

        {/* Grid Settings */}
        <PanelSection title="Grid Settings" defaultOpen={false}>
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
              Grid Size
            </label>
            <div className="flex gap-2">
              {[0.5, 1, 2].map((size) => (
                <button
                  key={size}
                  onClick={() => updateCanvas({ grid_size_m: size })}
                  className={cn(
                    'flex-1 px-2 py-1.5 text-xs rounded border transition-colors',
                    layout.canvas.grid_size_m === size
                      ? 'bg-blue-500 text-white border-blue-600'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
                  )}
                >
                  {size}m
                </button>
              ))}
            </div>
          </div>
        </PanelSection>

        {/* Summary Statistics */}
        <PanelSection title="Summary" defaultOpen={true}>
          <div className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
            <div className="flex justify-between">
              <span>Total Objects:</span>
              <span className="font-mono font-semibold">{layout.objects.length}</span>
            </div>
            <div className="flex justify-between">
              <span>Paper Targets:</span>
              <span className="font-mono font-semibold">
                {layout.objects.filter((o) => o.type === 'paper_target').length}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Steel Targets:</span>
              <span className="font-mono font-semibold">
                {layout.objects.filter((o) => o.type === 'steel_target').length}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Poppers:</span>
              <span className="font-mono font-semibold">
                {layout.objects.filter((o) => o.type === 'popper').length}
              </span>
            </div>
            <div className="flex justify-between">
              <span>No-Shoots:</span>
              <span className="font-mono font-semibold">
                {layout.objects.filter((o) => o.type === 'no_shoot').length}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Positions:</span>
              <span className="font-mono font-semibold">
                {layout.shooting_positions?.length || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Walls:</span>
              <span className="font-mono font-semibold">
                {layout.walls?.length || 0}
              </span>
            </div>
          </div>
        </PanelSection>

        {/* Stage Details */}
        <PanelSection title="Details" defaultOpen={false}>
          <TextInput
            label="Briefing"
            value={layout.metadata?.briefing || ''}
            onChange={(value) => updateMetadata({ briefing: value })}
            placeholder="Add stage briefing text..."
            multiline
            rows={4}
          />

          {/* Note: par_time and round_count are not defined on StageMetadata */}
          {/* These would need to be calculated or added to the scoring configuration */}
        </PanelSection>
      </div>
    </div>
  );
};

export default StageMetadataPanel;
